package fr.manyverse.app.infrastructure.repository

import fr.manyverse.app.AppLogger
import fr.manyverse.app.domain.model.ActiveCombat
import fr.manyverse.app.domain.model.CombatParticipant
import fr.manyverse.app.domain.model.CombatParticipantType
import fr.manyverse.app.domain.repository.CombatRepository
import fr.manyverse.app.infrastructure.error.mapToDomainException
import fr.manyverse.app.infrastructure.realtime.GameChange
import fr.manyverse.app.infrastructure.realtime.GameRealtimeChannel
import fr.manyverse.app.infrastructure.serialization.appJson
import io.github.jan.supabase.SupabaseClient
import io.github.jan.supabase.postgrest.from
import io.github.jan.supabase.postgrest.query.Columns
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.map
import kotlinx.coroutines.flow.mapNotNull
import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable
import kotlinx.serialization.builtins.ListSerializer
import kotlinx.serialization.json.JsonArray
import kotlinx.serialization.json.JsonElement
import kotlin.time.Clock
import kotlin.time.Instant

private const val TAG = "CombatRepository"
private const val COMBATS_TABLE = "game_table_combats"

/**
 * Implémentation Supabase de [CombatRepository].
 * Un seul combat actif au plus par table ; l'état est diffusé via Realtime
 * (INSERT = nouveau combat, UPDATE = changement de tour / réordonnancement / arrêt).
 */
class SupabaseCombatRepository(
    private val supabaseClient: SupabaseClient,
    private val gameChannel: GameRealtimeChannel
) : CombatRepository {

    private val json = appJson {
        ignoreUnknownKeys = true
        encodeDefaults = true
    }

    private fun targetsOf(participants: List<CombatParticipant>): List<String> =
        participants
            .filter { it.type == CombatParticipantType.CHARACTER }
            .mapNotNull { it.userId }
            .distinct()

    override suspend fun createCombat(
        tableId: String,
        createdBy: String,
        participants: List<CombatParticipant>
    ): Result<ActiveCombat> {
        return try {
            val insertDto = CombatInsertDto(
                tableId = tableId,
                createdBy = createdBy,
                participants = participants,
                targetParticipants = targetsOf(participants),
                activeIndex = 0,
                isActive = true
            )

            val combat = supabaseClient
                .from(COMBATS_TABLE)
                .insert(insertDto) {
                    select()
                }
                .decodeSingle<CombatDto>()

            Result.success(combat.toDomain())
        } catch (e: Exception) {
            AppLogger.warn(TAG, "Failed to create combat for table=$tableId")
            Result.failure(mapToDomainException(e, "table"))
        }
    }

    override suspend fun getActiveCombat(
        tableId: String
    ): Result<ActiveCombat?> {
        return try {
            val combats = supabaseClient
                .from(COMBATS_TABLE)
                .select(Columns.ALL) {
                    filter {
                        eq("table_id", tableId)
                        eq("is_active", true)
                    }
                }
                .decodeList<CombatDto>()

            Result.success(combats.firstOrNull()?.toDomain())
        } catch (e: Exception) {
            AppLogger.warn(TAG, "Failed to get active combat for table=$tableId")
            Result.failure(mapToDomainException(e, "table"))
        }
    }

    override suspend fun updateActiveIndex(
        combatId: String,
        activeIndex: Int
    ): Result<Unit> {
        return try {
            supabaseClient
                .from(COMBATS_TABLE)
                .update({
                    set("active_index", activeIndex)
                }) {
                    filter { eq("id", combatId) }
                }
            Result.success(Unit)
        } catch (e: Exception) {
            AppLogger.warn(TAG, "Failed to update active index for combat=$combatId")
            Result.failure(mapToDomainException(e, "table"))
        }
    }

    override suspend fun updateParticipants(
        combatId: String,
        participants: List<CombatParticipant>,
        activeIndex: Int,
        existingTargets: List<String>
    ): Result<Unit> {
        return try {
            // target_participants ne décroît jamais pendant un combat : on conserve les joueurs
            // déjà ciblés (Realtime + RLS) même si leur personnage est retiré, pour que leur bulle
            // reste ouverte jusqu'à l'arrêt du combat. On ne fait qu'y ajouter de nouveaux joueurs.
            val mergedTargets = (existingTargets + targetsOf(participants)).distinct()
            supabaseClient
                .from(COMBATS_TABLE)
                .update({
                    set("participants", participants)
                    set("target_participants", mergedTargets)
                    set("active_index", activeIndex)
                }) {
                    filter { eq("id", combatId) }
                }
            Result.success(Unit)
        } catch (e: Exception) {
            AppLogger.warn(TAG, "Failed to update participants for combat=$combatId")
            Result.failure(mapToDomainException(e, "table"))
        }
    }

    override suspend fun stopCombat(
        combatId: String
    ): Result<Unit> {
        return try {
            supabaseClient
                .from(COMBATS_TABLE)
                .update({
                    set("is_active", false)
                }) {
                    filter { eq("id", combatId) }
                }
            Result.success(Unit)
        } catch (e: Exception) {
            AppLogger.warn(TAG, "Failed to stop combat=$combatId")
            Result.failure(mapToDomainException(e, "table"))
        }
    }

    override fun observeActiveCombat(
        tableId: String,
        currentUserId: String
    ): Flow<ActiveCombat?> = gameChannel.changes(COMBATS_TABLE).mapNotNull { change ->
        val record = change.record ?: return@mapNotNull null
        try {
            when (change.type) {
                GameChange.Type.INSERT ->
                    parseCombatFromRecord(record, tableId, currentUserId)?.let { CombatUpdate(it) }

                GameChange.Type.UPDATE -> when {
                    extractString(record["table_id"]) != tableId -> null
                    // Combat arrêté : null le ferme chez tout le monde
                    extractBoolean(record["is_active"]) != true -> CombatUpdate(null)
                    !isConcerned(record, currentUserId) -> null
                    else -> parseCombatFromRecord(record, tableId, currentUserId)?.let { CombatUpdate(it) }
                }

                GameChange.Type.DELETE -> null
            }
        } catch (e: Exception) {
            AppLogger.error(TAG, "Failed to parse combat from real-time update", e)
            null
        }
    }.map { it.combat }

    private data class CombatUpdate(val combat: ActiveCombat?)

    private fun isConcerned(record: Map<String, Any?>, currentUserId: String): Boolean {
        val targets = extractStringList(json, record["target_participants"])
        val createdBy = extractString(record["created_by"])
        return currentUserId in targets || currentUserId == createdBy
    }

    private fun parseCombatFromRecord(
        record: Map<String, Any?>,
        expectedTableId: String,
        currentUserId: String
    ): ActiveCombat? {
        val tableId = extractString(record["table_id"]) ?: return null
        if (tableId != expectedTableId) return null

        val isActive = extractBoolean(record["is_active"]) ?: false
        if (!isActive) return null

        if (!isConcerned(record, currentUserId)) return null

        val id = extractString(record["id"]) ?: return null
        val createdBy = extractString(record["created_by"]) ?: return null
        val activeIndex = extractInt(record["active_index"]) ?: 0
        val targetParticipants = extractStringList(json, record["target_participants"])
        val participants = parseParticipants(record["participants"])
        val createdAtStr = extractString(record["created_at"])
        val createdAt = createdAtStr?.let {
            try {
                Instant.parse(it)
            } catch (e: Exception) {
                Clock.System.now()
            }
        } ?: Clock.System.now()

        return ActiveCombat(
            id = id,
            tableId = tableId,
            createdBy = createdBy,
            participants = participants,
            targetParticipants = targetParticipants,
            activeIndex = activeIndex,
            isActive = isActive,
            createdAt = createdAt
        )
    }

    private fun parseParticipants(value: Any?): List<CombatParticipant> = try {
        when (value) {
            null -> emptyList()
            is JsonArray -> json.decodeFromJsonElement(
                ListSerializer(CombatParticipant.serializer()),
                value
            )
            is JsonElement -> json.decodeFromString(
                ListSerializer(CombatParticipant.serializer()),
                value.toString()
            )
            is String -> json.decodeFromString(
                ListSerializer(CombatParticipant.serializer()),
                value
            )
            else -> emptyList()
        }
    } catch (e: Exception) {
        AppLogger.error(TAG, "Failed to parse participants (${value?.let { it::class.simpleName }})", e)
        emptyList()
    }

    @Serializable
    private data class CombatInsertDto(
        @SerialName("table_id")
        val tableId: String,
        @SerialName("created_by")
        val createdBy: String,
        @SerialName("participants")
        val participants: List<CombatParticipant>,
        @SerialName("target_participants")
        val targetParticipants: List<String>,
        @SerialName("active_index")
        val activeIndex: Int,
        @SerialName("is_active")
        val isActive: Boolean
    )

    @Serializable
    private data class CombatDto(
        @SerialName("id")
        val id: String,
        @SerialName("table_id")
        val tableId: String,
        @SerialName("created_by")
        val createdBy: String,
        @SerialName("participants")
        val participants: List<CombatParticipant>,
        @SerialName("target_participants")
        val targetParticipants: List<String>,
        @SerialName("active_index")
        val activeIndex: Int,
        @SerialName("is_active")
        val isActive: Boolean,
        @SerialName("created_at")
        val createdAt: Instant
    ) {
        fun toDomain(): ActiveCombat = ActiveCombat(
            id = id,
            tableId = tableId,
            createdBy = createdBy,
            participants = participants,
            targetParticipants = targetParticipants,
            activeIndex = activeIndex,
            isActive = isActive,
            createdAt = createdAt
        )
    }
}
